import { type CurrencyPairName, currencies } from '@/config/currencies';
import { logger } from '@/logging/logger';
import type { ProductName } from '@/models/product';
import type { Rfs } from '@/models/rfs';
import { type ExecWay, type MasterAmount, type SgWay, sideFromMasterAmount } from '@/models/way';
import { http } from '@/utils/http';
import { formatDateLocalized } from '@/utils/locale';
import { isDefined, isNotDefined } from '@sgme/fp';
import { format } from 'date-fns';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { v4 } from 'uuid';

export const getSpotDate = (currencyPair: string) => {
  const request: TradeCaptureDto = {
    idVersion: 0,
    changedFields: {
      currencyPair,
      legs: { 0: { productName: 'FxSpot' } },
    },
  };

  return http.post(`myfx/tradeCapture/${v4()}/FxSpot`, request).pipe(
    map((res) => res.response.result as TradeCaptureDto),
    map((resp) => resp.changedFields.legs['0'].maturityDate),
  );
};

export const getAmericanForwardTradeCapture = (rfs: Rfs) => {
  const { masterAmount, sellAmount, buyAmount, buyCurrency, sellCurrency, date } = rfs;

  const currencyPair = `${buyCurrency}/${sellCurrency}`;
  const isInverted = currencies[currencyPair as CurrencyPairName]?.isInverted ?? false;

  const request = {
    idVersion: 0,
    changedFields: {
      currencyPair,
      markupCurrency: buyCurrency,
      legs: {
        0: {
          productName: 'FxAmericanForward',
          side: sideFromMasterAmount(masterAmount, isInverted),
          amount1String: buyAmount !== 0 ? String(buyAmount) : undefined,
          amount2String: sellAmount !== 0 ? String(sellAmount) : undefined,
          cutOffMarketPlace: 'NY',
          deliveryDateString: formatDateLocalized(date, 'yyyy-MM-dd'),
          premiumPaymentAmountBidString: '0',
          premiumPaymentAmountAskString: '0',
        },
      },
    },
  };

  return http
    .post('tradeCaptureBatch/FxAmericanForward', [
      {
        productType: 'FxAmericanForward',
        tileId: v4(),
        sessionId: v4(),
        request,
      },
    ])
    .pipe(
      map((res) => {
        const tcResponse = Object.values(res.response)[0] as TradeCaptureDto & {
          error: unknown;
        };

        if (isNotDefined(tcResponse.error)) {
          return tcResponse;
        }
        logger.logError('Error with tradeCapture', tcResponse);
        throw tcResponse;
      }),
      catchError((err) => {
        logger.logError('Error with tradeCapture', err);
        throw err;
      }),
    );
};

export const getCashTradeCapture = (productName: ProductName, buyAmount: number, sellAmount: number, currencyPair: string, maturityDate: string) => {
  const request: TradeCaptureDto = {
    idVersion: 0,
    changedFields: {
      currencyPair,
      legs: {
        0: {
          productName,

          amount1String: buyAmount !== 0 ? String(buyAmount) : undefined,
          amount2String: sellAmount !== 0 ? String(sellAmount) : undefined,
          maturityDateString: productName !== 'FxSpot' ? maturityDate : undefined,
        },
      },
    },
  };

  return http.post(`myfx/tradeCapture/${v4()}/${productName}`, request).pipe(
    map((res) => {
      const tcResponse: TradeCaptureResponse = res.response;

      if (tcResponse.isSuccess) {
        return tcResponse.result;
      }
      logger.logError('Error with tradeCapture', tcResponse);
      throw tcResponse;
    }),
    catchError((err) => {
      logger.logError('Error with tradeCapture', err);
      throw err;
    }),
  );
};

export const getClosedDates = (currencyPair: string) =>
  http.post('myfx/dates/closed', [currencyPair]).pipe(
    map((res) => res.response),
    map((resp) => resp[currencyPair] as string[]),
  );

export const requestForAmericanForwardQuotes = (rfsId: string, counterpartyId: string, connectionId: string, rfs: Rfs) => {
  const { buyCurrency, sellCurrency, masterAmount, buyAmount, sellAmount, date, spotDate, callabilityStart } = rfs;
  let currencyPair: CurrencyPairName = `${buyCurrency}/${sellCurrency}`;

  const isInverted = currencies[currencyPair as CurrencyPairName]?.isInverted ?? false;

  if (isInverted) {
    currencyPair = `${sellCurrency}/${buyCurrency}`;
  }

  const payload: RfsRequestDto = {
    fxOptionProduct: {
      amountReference: null,
      amountReferenceCurrency: null,
      currencyPair: currencyPair ?? '',
      hedges: [],
      legs: [
        {
          callabilityStartDate: isDefined(callabilityStart) ? new Date(callabilityStart) : spotDate,
          premiumDeliveryDate: isDefined(callabilityStart) ? new Date(callabilityStart) : spotDate,
          callabilityStyle: spotDate > new Date(callabilityStart!) ? 'CallabilityWindow' : 'CallabilityAmerican',
          cutOffMarketPlace: 'NY',
          deliveryDate: date,
          expiryDate: date,
          id: v4(),
          notionalAmount: masterAmount === 'buyAmount' ? buyAmount : sellAmount,
          notionalCurrency: getNotionalCurrency(currencyPair, buyCurrency, sellCurrency, masterAmount),
          premiumBidValue: 0,
          premiumAskValue: 0, // TODO check if it's okay
          putOrCall: 'Call',
          settlementType: 'Physical',
          side: isInverted ? 'Sell' : 'Buy',
          strikeInDelta: null,
          strikePrice: null,
          strikeShortcut: null,
        },
      ],
      markupCurrency: getNotionalCurrency(currencyPair, buyCurrency, sellCurrency, masterAmount),
      premiumCurrency: 1, // in solve rate it's useless but we need to send it
      premiumType: 'AMOUNT',
      skipCreditCheck: false,
      isSolving: true,
      strategyType: 'AmericanForward',
    },
    forceTradable: true,
    replyToStream: connectionId,
    rfsId,
    requesterCounterpartyId: Number.parseInt(counterpartyId, 10),
  };

  return http.post('fxoAmericanForward/rfs/request', payload);
};

export const requestForCashQuotes = (
  rfsId: string,
  currency1: string,
  currency2: string,
  negociatedAmount: number,
  negociatedCurrency: 1 | 2,
  date: string,
  product: ProductName,
  counterpartyId: string,
  connectionId: string,
  tradeId?: string,
  rolloverNearDate?: string,
) => {
  const payload: RfsRequestDto = {
    fxCashProduct:
      product !== 'FxSwap'
        ? {
            currencyPair: `${currency1}/${currency2}`,
            negociatedAmount,
            negociatedCurrency,
            expiryDate: date,
            cashType: product,
            tradeReference: tradeId,
          }
        : undefined,
    fxSwapProduct:
      product === 'FxSwap'
        ? {
            swapType: 'FxSwap',
            farLeg: {
              negociatedAmount,
              valueDate: date,
            },
            nearLeg: {
              negociatedAmount,
              valueDate: rolloverNearDate ?? date,
            },
            currencyPair: `${currency1}/${currency2}`,
            negociatedCurrency,
            extendedSwapPrecision: true,
          }
        : undefined,
    forceTradable: true,
    replyToStream: connectionId,
    rfsId,
    requesterCounterpartyId: Number.parseInt(counterpartyId, 10),
  };
  return http.post('myfx/rfs/request', payload);
};

export const executeCashRfs = (
  rfsId: string,
  quoteId: string,
  priceWithMargin: number,
  forwardPoints: number,
  way: SgWay,
  connectionId: string,
  clientTimestamp: number,
  serverTimestamp: number,
) => {
  if (rfsId === undefined) {
    throw new Error('Trying to execute some RFS with undefined rfsId');
  }
  const timeNow = Date.now() + serverTimestamp - clientTimestamp;

  const execution: RfsExecuteDto = {
    replyToStream: connectionId,
    way: way === 'Buy' ? 'Bid' : 'Ask',
    rfsId,
    quoteId,
    forwardPoints,
    priceWithMargin,
    calculatedTimestamp: timeNow,
  };

  logger.logInformation(
    `Quote execution of Rfs Id ${rfsId}. Last price at: ${serverTimestamp}, execution click front at: ${timeNow}, execution click with delta at: ${execution.calculatedTimestamp}.`,
  );
  logger.logInformation('Execute', execution);

  return http.post('myfx/rfs/execute', execution);
};

export const executeSwapRfs = (
  rfsId: string,
  quoteId: string,
  spot: number,
  nearPrice: number,
  farPrice: number,
  nearPoints: number,
  farPointsWithoutMargin: number,
  farPointsWithMargin: number,
  way: SgWay,
  connectionId: string,
  clientTimestamp: number,
  serverTimestamp: number,
) => {
  if (rfsId === undefined) {
    throw new Error('Trying to execute some swap RFS with undefined rfsId');
  }
  const timeNow = Date.now() + serverTimestamp - clientTimestamp;

  const execution: RfsSwapExecuteDto = {
    replyToStream: connectionId,
    way: way === 'Buy' ? 'Bid' : 'Ask',
    rfsId,
    quoteId,
    spot,
    nearPrice,
    farPrice,
    nearPoints,
    farPointsWithoutMargin,
    farPointsWithMargin,
    calculatedTimestamp: timeNow,
  };

  logger.logInformation(
    `Quote swap execution of Rfs Id ${rfsId}. Last price at: ${serverTimestamp}, execution click front at: ${timeNow}, execution click with delta at: ${execution.calculatedTimestamp}.`,
  );
  logger.logInformation('Execute swap', execution);

  return http.post('myfx/rfs/fxSwap/execute', execution);
};

export const executeAmericanForwardRfs = (rfsId: string, quoteId: string, connectionId: string, clientTimestamp: number, serverTimestamp: number, email: string) => {
  if (rfsId === undefined) {
    throw new Error('Trying to execute some RFS with undefined rfsId');
  }
  const timeNow = Date.now() + serverTimestamp - clientTimestamp;

  const executionClickByFrontTimestamp = new Date().getTime();
  const userLocalDateTime = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");

  const execution: AmericanForwardQuoteExecutePayload = {
    replyToStream: connectionId,
    way: 'Ask',
    rfsId,
    quoteId,
    userLocalDateTime,
    email64NotificationList: btoa(email),
    lastQuoteFromBackTimestamp: serverTimestamp,
    lastQuoteReceivedByFrontTimestamp: clientTimestamp,
    executionClickByFrontTimestamp,
  };

  logger.logInformation(`Quote execution of Rfs Id ${rfsId}. Last price at: ${serverTimestamp}, execution click front at: ${timeNow}, execution click with delta at: ${timeNow}.`);
  logger.logInformation('Execute', execution);

  return http.post('fxOption/rfs/execute', execution, { 'X-Correlation-ID': rfsId });
};

export const cancelStreaming = (rfsId: string) => {
  // fire and forget
  http
    .post('myfx/rfs/cancel', {
      rfsId,
    })
    .pipe(
      catchError((err) => {
        logger.logError('Cancel rfs failed', err);
        return of(true);
      }),
    )
    .subscribe();

  return of(true);
};

export interface TradeCaptureResponse {
  error: unknown;
  isSuccess: boolean;
  result: TradeCaptureDto;
}

export interface TradeCaptureDto {
  idVersion: number;
  changedFields: {
    currencyPair?: string;
    isReadyToPrice?: boolean;
    isPriceObsolete?: boolean;
    legs: {
      0: Partial<TradeCaptureLeg>;
    };
  };
  errors?: unknown;
  warnings?: unknown;
}

interface TradeCaptureLeg {
  productName: ProductName;
  maturityDate: string;
  currencyPair: string;
  // isPriceObsolete: boolean;
  maturityDateString: string;
  // maturityDateTenor: string;
  // currency1: string;
  // currency2: string;
  // isNonDeliverable: false;
  // fixingCurrency: string;
  // possibleFixingCurrencies: { default: string; possible: string[] };
  // fixingDate: string;
  // amount1: number | null;
  amount1String: string | null;
  // amount2: number | null;
  amount2String: string | null;
  // isReadyToPrice: boolean;
}

interface RfsRequestDto {
  fxCashProduct?: Partial<FxCashProduct>;
  fxSwapProduct?: Partial<FxSwapProduct>;
  fxOptionProduct?: Partial<FxoAmericanForwardProduct>;
  replyToStream: string;
  rfsId: string;
  requesterCounterpartyId: number;
  forceTradable: true;
}

interface SwapLeg {
  price?: number;
  valueDate: string;
  negociatedAmount: number;
}

interface FxSwapProduct {
  swapType: 'FxSwap';
  currencyPair: string;
  negociatedCurrency: 1 | 2;
  farLeg: SwapLeg;
  nearLeg: SwapLeg;
  extendedSwapPrecision: true;
}

export interface FxoAmericanForwardProduct {
  strategyType: 'AmericanForward';
  currencyPair: string;
  premiumDeliveryDate?: Date;
  strategyNetted?: boolean;
  premiumCurrency: 1 | 2;
  premiumType: 'AMOUNT' | 'PERCENT' | 'PPS';
  amountReference: number | null;
  amountReferenceCurrency: string | null;
  markupCurrency: 1 | 2;
  legs: readonly FxoAmericanForwardLeg[];
  hedges: string[];
  skipCreditCheck: boolean;
  isSolving?: boolean;
}

export interface FxoAmericanForwardLeg {
  id: string;
  side: 'Buy' | 'Sell' | 'Both' | null;
  strikePrice?: number | null;
  strikeInDelta?: string | null;
  strikeShortcut?: string | null;
  notionalAmount: number;
  notionalCurrency: 1 | 2 | null;
  expiryDate: Date;
  deliveryDate: Date;
  premiumDeliveryDate?: Date;
  callabilityStartDate: Date;
  callabilityStyle: 'CallabilityWindow' | 'CallabilityAmerican';
  putOrCall: 'Call';
  settlementType: 'Physical';
  cutOffMarketPlace: string;
  premiumBidValue?: number | null;
  premiumAskValue?: number | null;
  markupBidValue?: number;
  markupAskValue?: number;
  volatilityAskValue?: number | null;
  volatilityBidValue?: number | null;
}

interface FxCashProduct {
  currencyPair: string;
  negociatedAmount: number;
  negociatedCurrency: 1 | 2;
  expiryDate: string;
  cashType: ProductName;
  tradeReference?: string;
}

interface RfsExecuteDto {
  replyToStream: string;
  rfsId: string;
  quoteId: string;
  way: ExecWay;
  priceWithMargin: number;
  forwardPoints: number;
  calculatedTimestamp: number;
}

interface RfsSwapExecuteDto {
  replyToStream: string;
  rfsId: string;
  quoteId: string;
  way: ExecWay;
  spot: number;
  nearPrice: number;
  farPrice: number;
  nearPoints: number;
  farPointsWithoutMargin: number;
  farPointsWithMargin: number;
  calculatedTimestamp: number;
}

export interface AmericanForwardQuoteExecutePayload {
  replyToStream: string;
  rfsId: string;
  quoteId: string;
  way: 'Ask';
  email64NotificationList: string;
  userLocalDateTime: string;
  lastQuoteFromBackTimestamp: number;
  lastQuoteReceivedByFrontTimestamp: number;
  executionClickByFrontTimestamp: number;
}

export const getNotionalCurrency = (currencyPair: string, buyCurrency: string, sellCurrency: string, masterAmount: string): 1 | 2 => {
  const [currency1, _currency2] = currencyPair.split('/');
  const isBuyAmount = masterAmount === 'buyAmount';
  const targetCurrency = isBuyAmount ? buyCurrency : sellCurrency;

  return currency1 === targetCurrency ? 1 : 2;
};
